// Medical Professional Portal — root component
// Handles sub-page routing + sidebar state; page bodies live in ./medical/*.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PortalLayout from '../../layouts/PortalLayout';
import markSrc from '../../assets/img/trustlab-mark.png';
import { SidebarNavItem, SidebarGroup, SidebarSubItem, BiIcon } from '../../layouts/SidebarNav';
import { ROUTES } from '../../constants/routes';
import { useAuth } from '../../context/AuthContext';
import { useListPortalComplaintsQuery } from '../../store/queriesApi';

// Sidebar icons are Bootstrap Icons, named as strings on the menu entries
// below — the same set AIMS's rail is drawn with. See SidebarNav.js for
// how the stylesheet gets loaded.

import PatientReports from './medical/PatientReports';
import ReportQueries from './medical/ReportQueries';
import SampleConcerns from './medical/SampleConcerns';
import RaiseQuery from './medical/RaiseQuery';
import CapaTracker from './medical/CapaTracker';
import RaiseTicket from './medical/RaiseTicket';
import MyTickets from './medical/MyTickets';
import MyDoctors from './medical/MyDoctors';
import Analytics from './medical/Analytics';
// Lab-portal pages — Medical Portal now shares the same dashboard +
// eDOS catalogue + TRF download surface as the Lab Partner Portal so
// doctors see the directory of services in exactly the way lab
// partners see it. The economics framing (L2L vs MRP) renders the
// same in both for transparency.
import LabOverview from './lab/LabOverview';
import EdosTests from './lab/EdosTests';
import EdosDepartments from './lab/EdosDepartments';
import EdosSpeciality from './lab/EdosSpeciality';
import EdosTrfForms from './lab/EdosTrfForms';
import PartnerFeedbackPage from './feedback/PartnerFeedbackPage';
import PartnerFeedbackModal from './feedback/PartnerFeedbackModal';
import ChangePasswordModal from '../../components/common/ChangePasswordModal';

// Bootstraps two things the first time the medical portal mounts:
//
//   1. Fetches Google's IBM Plex Sans + IBM Plex Mono @font-face CSS,
//      rewrites the family names to the private 'MedicalPortalSans' and
//      'MedicalPortalMono', injects as a <style> tag. The rest of the site
//      references 'Outfit' (not these names) so it stays untouched.
//      Fetching at runtime means we always get whatever URL versions
//      Google is currently serving.
//
//      IBM Plex is AIMS's type, and the pair matters: AIMS sets prose in
//      the sans and every identifier, count and figure in the mono, which
//      is what makes its registers line up column by column.
//   2. Injects a global rule that applies MedicalPortalSans to anything
//      under `body.medical-portal-active` — including MUI portalled
//      surfaces (Modal / Menu / Tooltip / Drawer) which render at the
//      document root, outside the wrapper Box, so an `& *` sx selector
//      can't reach them.
const FONT_TAG = 'data-medical-portal-font';
const RULE_TAG = 'data-medical-portal-rule';
const BODY_CLASS = 'medical-portal-active';

const loadMedicalPortalFont = () => {
  if (!document.querySelector(`style[${FONT_TAG}]`)) {
    fetch('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600;700&display=swap')
      .then((r) => (r.ok ? r.text() : Promise.reject()))
      .then((css) => {
        // Two families, two private names. Both re-declarations have to
        // happen in this one stylesheet: Google returns them interleaved in
        // a single response, so splitting the fetch would double the
        // round-trips for no gain.
        const scoped = css
          .replace(/font-family:\s*'IBM Plex Sans'/g, "font-family: 'MedicalPortalSans'")
          .replace(/font-family:\s*'IBM Plex Mono'/g, "font-family: 'MedicalPortalMono'");
        const style = document.createElement('style');
        style.setAttribute(FONT_TAG, '');
        style.textContent = scoped;
        document.head.appendChild(style);
      })
      .catch(() => { /* font fetch failed; portal falls back to sans-serif */ });
  }

  // Global rule — applies to the portal tree AND any MUI-portaled surfaces.
  // Scoped by the `body.medical-portal-active` marker so it only takes
  // effect while MedicalDashboard is mounted.
  if (!document.querySelector(`style[${RULE_TAG}]`)) {
    const rule = document.createElement('style');
    rule.setAttribute(RULE_TAG, '');
    // EVERY SELECTOR IS WRAPPED IN `:where()`, WHICH IS THE POINT.
    //
    // `!important` is unavoidable here — it is the only thing that beats
    // MUI's own component font-family on the portalled surfaces. But an
    // `!important` on a selector like `body.x *` also beats every sx prop
    // in the portal, which silently flattened all 179 `fontFamily: MONO`
    // sites to sans. `:where()` contributes zero specificity, so this rule
    // still wins against MUI (which sets no `!important`) while LOSING to
    // the `!important` baked into MONO in medical/shared.js. That one-sided
    // outcome is what keeps table headers, IDs and figures monospaced.
    rule.textContent = `
      :where(body.${BODY_CLASS}),
      :where(body.${BODY_CLASS}) *,
      :where(body.${BODY_CLASS} .MuiPopover-root),
      :where(body.${BODY_CLASS} .MuiModal-root),
      :where(body.${BODY_CLASS} .MuiDrawer-root),
      :where(body.${BODY_CLASS} .MuiTooltip-popper),
      :where(body.${BODY_CLASS} .MuiMenu-root),
      :where(body.${BODY_CLASS} .MuiPopover-root) *,
      :where(body.${BODY_CLASS} .MuiModal-root) *,
      :where(body.${BODY_CLASS} .MuiDrawer-root) *,
      :where(body.${BODY_CLASS} .MuiTooltip-popper) *,
      :where(body.${BODY_CLASS} .MuiMenu-root) * {
        font-family: 'MedicalPortalSans', system-ui, sans-serif !important;
      }

      /* NO RUBBER-BAND. Dragging past the top or bottom of the portal used
         to bounce the whole page, which on a dense register reads as the
         table coming loose.

         Two rules, deliberately separate. The first is the one that does
         the work: overscroll-behavior set on BODY propagates to the
         viewport when the root element leaves its own value at 'auto',
         which is the case here. The second says the same thing on the root
         for engines that want it there - split out because :has() is the
         newer feature of the two, and an unsupported selector invalidates
         only its own rule, so the body rule survives either way.

         Both are scoped to the portal's body class, which the dashboard
         removes on unmount, so the rest of the site scrolls as it always
         has. */
      body.${BODY_CLASS} { overscroll-behavior: none; }
      html:has(> body.${BODY_CLASS}) { overscroll-behavior: none; }

      /* And a scroller inside the portal — a long table, the sidebar —
         keeps its overscroll to itself instead of handing it to the page
         once it hits its own end. */
      body.${BODY_CLASS} [class*='MuiDrawer-paper'],
      body.${BODY_CLASS} [class*='MuiDialog-paper'],
      body.${BODY_CLASS} [class*='MuiDialogContent-root'] {
        overscroll-behavior: contain;
      }

      /* LOADING PLACEHOLDERS.
         MUI's "wave" variant sweeps a white gradient across each bar. With
         a dozen bars on screen that is a dozen highlights travelling at
         once, which draws the eye to the waiting rather than to the page.
         The bars are now AIMS's own --rule-2, pulsing slowly in place:
         present enough to say "this is coming", quiet enough to ignore. */
      body.${BODY_CLASS} .MuiSkeleton-root {
        background-color: #EDF1F4;
      }
      /* Belt and braces - the sweep is drawn by a pseudo-element, so a
         stray animation="wave" anywhere still renders as a calm pulse. */
      body.${BODY_CLASS} .MuiSkeleton-wave::after {
        display: none;
      }
      body.${BODY_CLASS} .MuiSkeleton-wave {
        animation: mp-skeleton-pulse 1.6s ease-in-out .5s infinite;
      }
      @keyframes mp-skeleton-pulse {
        0%, 100% { opacity: 1; }
        50%      { opacity: .45; }
      }
      /* Someone who has asked their system for less motion gets none. */
      @media (prefers-reduced-motion: reduce) {
        body.${BODY_CLASS} .MuiSkeleton-root {
          animation: none !important;
          opacity: .75;
        }
      }
    `;
    document.head.appendChild(rule);
  }
};

// MUI's own accent inside this portal.
//
// Everything the screens draw by hand already reads from MP.*, but the bits
// MUI colours for itself — a focus ring, a checkbox, a Switch, the selected
// page in a Pagination — come from `palette.primary`, which is the PUBLIC
// site's sage green. That left AIMS-teal screens sprouting sage controls.
//
// The function form of `theme` merges into the outer theme rather than
// replacing it, so typography, breakpoints and every component override the
// app sets up are untouched. `light`/`dark`/`contrastText` are given
// explicitly: a partial palette merge keeps the OUTER theme's derived
// values, so overriding `main` alone would leave sage hovers behind.
const aimsPalette = (outer) => createTheme(outer, {
  palette: {
    primary:   { main: '#0F766E', light: '#2B9E8A', dark: '#0C5F58', contrastText: '#FFFFFF' },
    secondary: { main: '#B4700B', light: '#D08E22', dark: '#8A5508', contrastText: '#FFFFFF' },
    error:     { main: '#B3261E', light: '#D1453C', dark: '#8E1E17', contrastText: '#FFFFFF' },
    warning:   { main: '#B4700B', light: '#D08E22', dark: '#8A5508', contrastText: '#FFFFFF' },
    success:   { main: '#1E7A45', light: '#2E9C5C', dark: '#166135', contrastText: '#FFFFFF' },
    info:      { main: '#1B5E9E', light: '#2E7CC4', dark: '#144A7D', contrastText: '#FFFFFF' },
  },
  // --r. MUI multiplies this by its own factors, so 6 here gives 6px on a
  // Button and Paper, matching the hand-rolled cards.
  shape: { borderRadius: 6 },
});

// Today's date — rendered in the topbar as "Fri, 17 Apr, 2026"
const TODAY_LABEL = (() => {
  const d = new Date();
  const wk = d.toLocaleDateString('en-GB', { weekday: 'short' });        // "Fri"
  const dy = d.toLocaleDateString('en-GB', { day: '2-digit' });          // "17"
  const mo = d.toLocaleDateString('en-GB', { month: 'short' });          // "Apr"
  const yr = d.getFullYear();                                            // 2026
  return `${wk}, ${dy} ${mo}, ${yr}`;
})();

// Sidebar menu — MUI outline icons, sized to fit SidebarNavItem's 20px
// icon slot. Items with `hidden: true` are kept in the list (and the
// route still works) but don't render in the sidebar. Flip the flag
// to `false` or delete the key to bring them back.
//
// "Report Queries" badge is filled in dynamically inside the component
// from the live RCA list — anything not resolved/closed is "active"
// and worth alerting on. The static menu is just the layout skeleton.
// Each entry carries a Bootstrap icon name and a `tint`. The tints are
// AIMS's rail palette: every module there has its own colour so the list
// is navigable by hue before the labels are read, and the marker beside
// the current row takes that colour instead of being gold for all of them.
// They are deliberately all light — they sit on a dark teal rail.
//
// MENU is split into two halves so the eDOS collapsible group can sit
// between Dashboard and the rest of the items. Mirrors LabDashboard's
// layout so the two portals' sidebars feel like one product.
const TOP_MENU = [
  { id: 'dashboard',   icon: 'speedometer2',              tint: '#FF7A45', label: 'Dashboard' },
];
const BOTTOM_MENU = [
  { id: 'reports',     icon: 'file-earmark-medical-fill', tint: '#5FD3F3', label: 'Patient Reports' },
  // Centre-scoped referring doctors. Reads from /api/portal/ref-doctors
  // — independent of IT Dose since the vendor exposes no doctor master
  // endpoint. Admin can Add/Edit/Delete; harvest hook auto-populates
  // when the IT Dose webhook starts shipping doctor names.
  { id: 'doctors',     icon: 'people-fill',               tint: '#7FB3FF', label: 'My Doctors' },
  // Test Requisition is no longer hidden — same as the Lab Partner
  // Portal. Lands on the EdosTrfForms branch-specific TRF download
  // page so doctors can grab a printable form to attach to samples.
  { id: 'requisition', icon: 'clipboard-pulse',           tint: '#4FD1A5', label: 'Test Requisition' },
  { id: 'queries',     icon: 'exclamation-triangle-fill', tint: '#FF9E4A', label: 'Report Queries' },
  // Samples TrustLab received from this hospital's wards without a label or
  // proper information (TL-CSD-SCR, logged in Trust OPM). Centre login only:
  // not in DOCTOR_MENU_IDS.
  { id: 'concerns',    icon: 'clipboard2-x-fill',          tint: '#FF6B6B', label: 'Sample Concerns' },
  // Hidden from the sidebar: Report Queries carries a New Query button,
  // and two doors to the same form a few hundred pixels apart is one too
  // many. `hidden` keeps the entry in MENU, so the route, SECTION_IDS and
  // every go('new-query') caller still work — it just isn't listed.
  { id: 'new-query',   icon: 'file-earmark-plus-fill',    tint: '#FFC81F', label: 'Raise Query', hidden: true },
  // Read-only CAPA records from QTQMS, scoped to the queries this user
  // raised. Sits under Raise Query because it is the downstream half of
  // the same story: you raise a query, the lab opens a CAPA against it.
  // Hidden from the sidebar on request. Same mechanism as Raise Query
  // above: the entry stays in MENU so SECTION_IDS still accepts
  // ?section=capa and the body dispatcher still renders it.
  //
  // NOTE, because this differs from Raise Query: that one is still
  // reachable from the New Query button on Report Queries. CAPA has no
  // other entry point in the UI, so hiding the row leaves it reachable
  // only by typing the URL. Give it a link somewhere if it should stay
  // discoverable.
  { id: 'capa',        icon: 'patch-check-fill',          tint: '#7ED957', label: 'CAPA Tracker', hidden: true },
  // Internal CRM ticket (stock / equipment / billing / IT / HR …).
  // Different surface from Raise Query — that one is for report-level
  // QA queries; this one is for branch-operations issues.
  // Both hidden: a complaint is now raised through Raise Query, which
  // registers it in Trust OPM alongside the QTQMS query, and its status
  // and replies show on the Report Queries page. The screens stay
  // reachable by id so nothing that links to them breaks.
  { id: 'new-ticket',  icon: 'ticket-detailed-fill',      tint: '#B39DFF', label: 'Raise Ticket', hidden: true },
  { id: 'my-tickets',  icon: 'journal-bookmark-fill',     tint: '#FF7FB6', label: 'My Tickets', hidden: true },
  // Voice of Partner — feedback submission for the active cycle. The
  // auto-popup modal also fires from this dashboard, so the menu
  // entry is the partner's manual route back if they dismissed it.
  { id: 'feedback',    icon: 'megaphone-fill',            tint: '#FF8A80', label: 'TrustLab Feedback' },
  // Reports & Analytics pinned last per medical-portal sidebar spec.
  { id: 'analytics',   icon: 'bar-chart-line-fill',       tint: '#2EB1FF', label: 'Reports & Analytics' },
];
// Combined menu used for activeMenu / topbar lookups.
const MENU = [...TOP_MENU, ...BOTTOM_MENU];

// ── How the rail is arranged ──────────────────────────────────
// Describes GROUPING ONLY, by id. MENU above stays the flat source of truth
// for labels, icons, routes and the doctor allowlist, so rearranging the
// rail can never knock out a route or a title — the two concerns are kept
// apart deliberately.
//
// `items` not listed anywhere here render as standalone rows after the
// groups, in MENU order.
const NAV_GROUPS = [
  {
    key: 'overview',
    label: 'Overview',
    icon: 'speedometer2',
    tint: '#FF7A45',
    items: ['dashboard', 'analytics'],
  },
  {
    key: 'tests',
    label: 'Test',
    icon: 'clipboard-pulse',
    tint: '#4FD1A5',
    items: ['edos-tests', 'edos-departments', 'edos-speciality', 'requisition'],
  },
  {
    key: 'patients',
    label: 'Patient Reports',
    icon: 'file-earmark-medical-fill',
    tint: '#5FD3F3',
    items: ['reports', 'doctors', 'queries', 'concerns'],
  },
];

// Everything that lives in a group, so the standalone pass can skip them.
const GROUPED_IDS = new Set(NAV_GROUPS.flatMap((g) => g.items));

// Which group an id belongs to, so the right one can be opened when a deep
// link lands inside it.
const GROUP_OF = Object.fromEntries(
  NAV_GROUPS.flatMap((g) => g.items.map((id) => [id, g.key])),
);

// Labels for the eDOS children read better with their prefix inside a
// group that is already called "Test".
const GROUP_ITEM_LABELS = {
  'edos-tests': 'eDOS · Tests',
  'edos-departments': 'eDOS · Departments',
  'edos-speciality': 'eDOS · Speciality',
  requisition: 'Test Requisition Form',
};

// Every id the portal can show. Used to validate `?section=` off the URL so a
// stale or hand-typed value falls back to the dashboard instead of rendering
// a blank panel. The eDOS children are listed explicitly because EDOS_GROUP
// is declared inside the component.
const SECTION_IDS = new Set([
  ...MENU.map((m) => m.id),
  'edos-tests', 'edos-departments', 'edos-speciality',
]);

// Doctor-mode sidebar allowlist. When the user logged in via the
// "Login as Doctor" fork, only these IDs remain in the sidebar.
// Everything else (Test Requisition, Raise Ticket, eDOS group, My
// Doctors) is hidden because it's an operations / centre-admin
// surface that the doctor doesn't need. `dashboard` lives in TOP_MENU
// so it's always available regardless.
const DOCTOR_MENU_IDS = new Set([
  'dashboard',
  'reports',     // Patient Reports
  'queries',     // Report Queries
  'new-query',   // Raise Query
  // Raise Ticket / My Tickets intentionally EXCLUDED — the helpdesk
  // workflow (operational issues, billing, equipment) is a centre-
  // admin surface. Doctors only have report-level concerns, which
  // go through Raise Query instead.
  'feedback',    // TrustLab Feedback
  'analytics',   // Reports & Analytics — doctor-scoped via the same
                 // salesData hook the dashboard tiles use.
]);

// eDOS sidebar group — parent + three searchable children. Same shape
// as LabDashboard's EDOS_GROUP so the two portals share the eDOS
// surface entirely (the underlying React components are the same).
const EDOS_GROUP = [
  { id: 'edos-tests',       icon: 'droplet-fill',    tint: '#4FD1A5', label: 'Tests' },
  { id: 'edos-departments', icon: 'diagram-3-fill',  tint: '#B39DFF', label: 'Departments' },
  { id: 'edos-speciality',  icon: 'heart-pulse-fill', tint: '#FF8A80', label: 'Speciality' },
];

// One lookup for every rail row, MENU plus the eDOS children. The two were
// always separate lists; the rail now renders them the same way, so it needs
// a single place to resolve an id.
const MENU_BY_ID = Object.fromEntries(
  [...MENU, ...EDOS_GROUP].map((m) => [m.id, m]),
);

// view_status_label → "active" classification for the queries badge.
// Anything not in this set (resolved / closed / unknown) is excluded,
// so the badge tracks queries that still need attention.
// OPM statuses that still need attention: open, assigned, in_progress.
const isActiveQuery = (rec) => rec?.active === true;

const TITLES = {
  dashboard:   'Dashboard',
  reports:     'Patient Reports',
  doctors:     'My Doctors',
  requisition: 'Test Requisition Form',
  queries:     'Report Queries',
  concerns:    'Sample Concerns',
  'new-query': 'Raise a Query',
  capa:        'CAPA Tracker',
  'new-ticket':'Raise a Ticket',
  'my-tickets':'My Tickets',
  analytics:   'Reports & Analytics',
  feedback:    'TrustLab Feedback',
  'edos-tests':       'eDOS · Tests',
  'edos-departments': 'eDOS · Departments',
  'edos-speciality':  'eDOS · Speciality',
};


const MedicalDashboard = () => {
  // The open section lives in the URL (`?section=new-query`), not in
  // component state.
  //
  // It used to be `useState('dashboard')`, which meant a refresh anywhere in
  // the portal dumped you back on the Dashboard — hit F5 half-way through
  // Raise a Query and the page was gone. It also made every section
  // unlinkable and broke the browser's Back button, since the URL never
  // changed as you moved around.
  //
  // `active` is DERIVED from the URL rather than mirrored into state, so
  // there is one source of truth and no chance of the two disagreeing.
  // `setActive` keeps its old signature, so every existing call site works
  // unchanged.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedSection = searchParams.get('section') || '';
  const active = SECTION_IDS.has(requestedSection) ? requestedSection : 'dashboard';

  const setActive = useCallback((next) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      // Dashboard is the default, so it stays out of the URL and
      // /portal/medical keeps working as the clean entry point.
      if (!next || next === 'dashboard') params.delete('section');
      else params.set('section', next);
      return params;
    });
  }, [setSearchParams]);
  // Which rail groups are expanded. EVERY GROUP STARTS OPEN: the rail is
  // short enough to show whole, and making someone click a category open
  // before they can see what is in it costs a click and hides the map of
  // the product. The chevrons still collapse a group by hand for anyone who
  // wants the room.
  const [openGroups, setOpenGroups] = useState(
    () => Object.fromEntries(NAV_GROUPS.map((g) => [g.key, true])),
  );
  useEffect(() => {
    const g = GROUP_OF[active];
    if (g) setOpenGroups((o) => (o[g] ? o : { ...o, [g]: true }));
  }, [active]);
  // Holds the visit object passed up from ReportDetailModal → Overview's
  // onNavigate('new-complaint', { visit }). RaiseQuery reads this to
  // pre-select the patient. Cleared whenever the user navigates to
  // anything other than 'new-query' so the next manual visit doesn't
  // get a stale prefill.
  const [prefillVisit, setPrefillVisit] = useState(null);
  const navigate = useNavigate();
  // `isDoctor` + `doctor` come from AuthContext when the centre user
  // logged in via the "Login as Doctor" fork on the role-select modal.
  // The portal shell stays identical; we only swap the display name in
  // the rail's brand row so the trustian instantly sees they're in
  // doctor-scoped mode.
  const { user, isDoctor, doctor } = useAuth();
  const displayName    = isDoctor && doctor?.name
    ? `Dr. ${doctor.name.replace(/^Dr\.?\s+/i, '')}`
    : (user?.name || 'Guest');
  const userName       = displayName;

  // Live "Report Queries" badge — counts queries that still need
  // attention (received / investigating). RTK Query auto-refreshes
  // this whenever a new query is submitted (createRcaInvestigation
  // invalidates the 'Rca' tag), so the badge stays in sync without
  // any manual refetch here.
  // Read from Trust OPM, the same list the Report Queries page shows.
  const { data: rcaListData } = useListPortalComplaintsQuery(
    { clientId: user?.client_id, per_page: 5 },
    { skip: !user?.client_id },
  );
  const activeQueriesCount = rcaListData?.counts?.active ?? (rcaListData?.records || []).filter(isActiveQuery).length;

  // On mount: ensure the scoped font + global CSS rule are registered,
  // and tag the document body so the rule takes effect. On unmount: drop
  // the tag — the injected <style> tags stay (harmless once detagged).
  useEffect(() => {
    loadMedicalPortalFont();
    document.body.classList.add(BODY_CLASS);
    return () => {
      document.body.classList.remove(BODY_CLASS);
    };
  }, []);

  // Sidebar footer button — lets the doctor hop back to the public site
  // instead of signing out. Rendered as a custom slot so the default
  // Sign Out button on other portals (Lab / Corp / Admin) stays intact.
  const backToSite = (
    <Button
      fullWidth
      onClick={() => navigate(ROUTES.HOME)}
      startIcon={<ArrowBackIcon />}
      sx={{
        color: 'rgba(255,255,255,0.75)',
        bgcolor: 'rgba(16,21,28,.10)',
        border: '1px solid rgba(16,21,28,.10)',
        borderRadius: 2,
        fontSize: 13,
        fontWeight: 700,
        textTransform: 'none',
        letterSpacing: '0.2px',
        transition: 'all 0.2s',
        '&:hover': {
          bgcolor: '#0F766E',
          borderColor: '#0F766E',
          color: '#fff',
        },
      }}
    >
      Back to Website
    </Button>
  );

  // Alias used by Overview -> navigate back/forward.
  // `params` is optional and currently carries `{ visit }` from the
  // "Raise Query" button in ReportDetailModal — we stash it so
  // RaiseQuery can pre-select the patient on mount.
  // Memoised so it is stable across renders: topbarAction depends on it, and
  // an unstable `go` would rebuild that memo on every render.
  const go = useCallback((key, params = {}) => {
    const map = {
      'reports': 'reports',
      'new-complaint': 'new-query',
    };
    const next = map[key] || key;
    setPrefillVisit(next === 'new-query' ? (params.visit || null) : null);
    setActive(next);
  }, [setActive]);

  // Right-side top-bar action — changes based on current page
  const topbarAction = useMemo(() => {
    switch (active) {
      case 'queries':
        return (
          <Button variant="contained" size="small" onClick={() => go('new-query')}
            sx={{ bgcolor: '#0F766E', color: '#FFFFFF', '&:hover': { bgcolor: '#0C5F58' } }}>
            + New Query
          </Button>
        );
      case 'new-query':
        return (
          <Button variant="outlined" size="small" onClick={() => go('queries')}>
            ← Back
          </Button>
        );
      case 'new-ticket':
        return (
          <Button variant="outlined" size="small" onClick={() => go('dashboard')}>
            ← Back
          </Button>
        );
      // 'my-tickets' deliberately has no topbar action: the screen's own
      // card header already carries a New Ticket button, and two of them a
      // few hundred pixels apart is one too many.
      case 'requisition':
        return (
          <Button variant="outlined" size="small">📋 My Requisitions</Button>
        );
      case 'analytics':
        return (
          <Button variant="outlined" size="small">⬇ Export PDF</Button>
        );
      default:
        return null;
    }
  }, [active, go]);

  // Page body dispatcher
  const body = (() => {
    switch (active) {
      case 'dashboard':
        return <LabOverview onNavigate={go} />;
      case 'reports':
        return <PatientReports onNavigate={go} />;
      case 'doctors':
        return <MyDoctors />;
      case 'requisition':
        return <EdosTrfForms />;
      case 'queries':
        return <ReportQueries
          onOpenComplaint={() => {}}
          onNavigate={go}
        />;
      case 'concerns':
        return <SampleConcerns />;
      case 'capa':
        return <CapaTracker />;
      case 'new-query':
        return (
          <RaiseQuery
            prefillVisit={prefillVisit}
            onBack={() => { setPrefillVisit(null); setActive('queries'); }}
          />
        );
      case 'new-ticket':
        return (
          <RaiseTicket
            onBack={() => setActive('dashboard')}
            // After a successful POST, jump to the My Tickets list so
            // the user sees the new ticket at the top — better feedback
            // than just falling back to the dashboard.
            onCreated={() => setActive('my-tickets')}
          />
        );
      case 'my-tickets':
        return (
          <MyTickets
            // Lets the empty / refresh action launch a new ticket
            // without making the user hunt for the sidebar entry.
            onRaise={() => setActive('new-ticket')}
          />
        );
      case 'analytics':
        return <Analytics />;
      case 'feedback':
        return <PartnerFeedbackPage />;
      case 'edos-tests':
        return <EdosTests />;
      case 'edos-departments':
        return <EdosDepartments />;
      case 'edos-speciality':
        return <EdosSpeciality />;
      default:
        return null;
    }
  })();

  // Font is applied via the `body.medical-portal-active` class set in the
  // useEffect above. That approach (vs a Box wrapper) reaches MUI Modal /
  // Menu / Tooltip portals which render at the document root and would
  // otherwise escape any ancestor-based sx selector.
  // Rich topbar title — icon tile matching the active route, big title
  // with a teal gradient accent underline, and a small eyebrow tag.
  // Fades in on every route change so switching menus feels responsive.
  // Look up the active menu entry — first the top-level MENU, then
  // the eDOS sub-group, so the topbar icon + title still resolve when
  // the user is inside a Search-By view.
  const activeMenu = MENU_BY_ID[active];
  // The topbar tile shows the current page's glyph in the brand teal
  // rather than its rail tint — on white, the rail colours are far too
  // light to read.
  const activeIcon = activeMenu?.icon
    ? <BiIcon name={activeMenu.icon} size={18} color="#0F766E" />
    : null;

  const medicalTopbarTitle = (
    <Stack
      key={active}
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{
        minWidth: 0,
        animation: 'mp-topbar-fade 0.3s ease-out',
        '@keyframes mp-topbar-fade': {
          from: { opacity: 0, transform: 'translateX(-4px)' },
          to:   { opacity: 1, transform: 'translateX(0)' },
        },
      }}
    >
      {/* Icon tile — tinted teal square that matches the sidebar glyph */}
      <Box
        sx={{
          width: 38, height: 38,
          borderRadius: '10px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #E6F2F1 0%, #D4EFEB 100%)',
          border: '1px solid #A8CCC8',
          boxShadow: '0 2px 6px rgba(16,21,28,.10)',
        }}
      >
        {activeIcon}
      </Box>

      {/* Title + eyebrow — flips to "Doctor Portal · <Centre>" when
          the user logged in via the "Login as Doctor" fork so the
          context is unambiguous at every glance. */}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 9.5,
            fontWeight: 700,
            color: '#0F766E',
            textTransform: 'uppercase',
            letterSpacing: '0.16em',
            lineHeight: 1,
          }}
        >
          {isDoctor
            ? `Doctor Portal${user?.name ? ` · ${user.name}` : ''}`
            : 'Medical Portal'}
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: 16, md: 20 },
            fontWeight: 700,
            color: '#10151C',
            letterSpacing: '-0.2px',
            lineHeight: 1.2,
            mt: 0.5,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            // Subtle teal gradient accent on the left edge — a tiny
            // design touch that makes the header feel branded without
            // being loud.
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: -10,
              top: '15%',
              bottom: '15%',
              width: 3,
              borderRadius: 2,
              background: 'linear-gradient(180deg, #0F766E 0%, #0C5F58 100%)',
              display: { xs: 'none', md: 'block' },
            },
          }}
        >
          {TITLES[active] || 'Overview'}
        </Typography>
      </Box>
    </Stack>
  );

  return (
    <ThemeProvider theme={aimsPalette}>
      <PortalLayout
        brand="TrustLab"
        brandSub="Diagnostics Portal"
        // The mark on its own (not the full lockup) with the signed-in
        // name beside it — AIMS's `.rail-brand`: one 56px row that says
        // whose product this is and who is using it. The name used to sit
        // in a card at the foot of the rail, which put the two pieces of
        // standing context at opposite ends of a 100vh column.
        brandSlot={(
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
            <Box
              component="img"
              src={markSrc}
              alt="TrustLab Diagnostics"
              sx={{ width: 38, height: 38, objectFit: 'contain', flexShrink: 0, display: 'block' }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 13.5,
                  fontWeight: 700,
                  color: '#fff',
                  lineHeight: 1.3,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {userName}
              </Typography>
              <Typography
                sx={{
                  fontSize: 9,
                  fontWeight: 600,
                  letterSpacing: '.155em',
                  textTransform: 'uppercase',
                  color: 'rgba(255,255,255,0.45)',
                  mt: '2px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {isDoctor ? (doctor?.specialization || 'Doctor') : 'Diagnostics Portal'}
              </Typography>
            </Box>
          </Stack>
        )}
        footerSlot={backToSite}
        // The wordmark and the brand glyph sit ON the rail, so the accent
        // has to be a LIGHT teal — AIMS's --rail-mark. The portal's actual
        // brand teal (MP.teal) is now dark enough to carry white text,
        // which also makes it invisible against the rail.
        accent="#BFEDE7"
        // AIMS's rail, reproduced exactly: a 168° dark-teal ramp with a
        // radial highlight thrown up from the bottom-left corner. The
        // highlight is what stops a 240px column of flat colour from
        // reading as a dead block beside the white canvas.
        sidebarBg={`
          radial-gradient(120% 50% at 0% 100%, rgba(43,158,138,.26) 0%, rgba(43,158,138,0) 62%),
          linear-gradient(168deg, #08322B 0%, #0B4239 34%, #0E4F45 70%, #136055 100%)
        `}
        topbarTitle={TITLES[active] || (isDoctor ? 'Doctor Portal' : 'Medical Portal')}
        topbarTitleSlot={medicalTopbarTitle}
        topbarRight={
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography
              sx={{
                fontSize: 12,
                color: '#4A5763',
                whiteSpace: 'nowrap',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              {TODAY_LABEL}
            </Typography>
            {topbarAction}
          </Stack>
        }
        sidebar={
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
            {(() => {
              // Visible, and allowed for this user. Doctor mode trims the
              // menu to DOCTOR_MENU_IDS; `hidden` entries stay routable but
              // are not listed.
              const visible = (id) => {
                const m = MENU_BY_ID[id];
                if (!m || m.hidden) return false;
                return !isDoctor || DOCTOR_MENU_IDS.has(id);
              };

              const renderLeaf = (m) => (
                <SidebarNavItem
                  key={m.id}
                  {...m}
                  // Live count on Report Queries only. `undefined`, not 0 —
                  // SidebarNavItem skips the pill entirely for undefined,
                  // and "0 things need you" is not worth a badge.
                  badge={m.id === 'queries'
                    ? (activeQueriesCount > 0 ? activeQueriesCount : undefined)
                    : m.badge}
                  iconColor={m.tint}
                  active={active === m.id}
                  onClick={() => go(m.id)}
                />
              );

              return (
                <>
                  {NAV_GROUPS.map((g) => {
                    const items = g.items.filter(visible);
                    // A group whose every child is hidden or not permitted
                    // would otherwise open onto nothing.
                    if (items.length === 0) return null;
                    const hasActive = items.includes(active);
                    return (
                      <SidebarGroup
                        key={g.key}
                        icon={g.icon}
                        iconColor={g.tint}
                        label={g.label}
                        active={hasActive}
                        open={!!openGroups[g.key]}
                        onToggle={() => setOpenGroups((o) => ({ ...o, [g.key]: !o[g.key] }))}
                      >
                        {items.map((id) => {
                          const m = MENU_BY_ID[id];
                          return (
                            <SidebarSubItem
                              key={id}
                              icon={m.icon}
                              iconColor={m.tint}
                              label={GROUP_ITEM_LABELS[id] || m.label}
                              active={active === id}
                              onClick={() => go(id)}
                            />
                          );
                        })}
                      </SidebarGroup>
                    );
                  })}

                  {/* Anything not placed in a group, in MENU order. */}
                  {MENU.filter((m) => !GROUPED_IDS.has(m.id) && visible(m.id)).map(renderLeaf)}
                </>
              );
            })()}
          </Box>
        }
      >
        {body}
      </PortalLayout>

      {/* Voice of Partner — auto-pops on dashboard mount when the
          active cycle is open and this partner hasn't submitted yet.
          The component owns its own visibility logic from the mock
          state, so dropping it here is enough. */}
      <PartnerFeedbackModal />

      {/* Forced change-password modal — pops the moment the user
          lands on this portal if they haven't set their own
          password yet. Field semantics:
            must_change_password === false  → not yet changed → SHOW modal
            must_change_password === true   → already changed → don't show
            must_change_password === undefined (legacy) → don't show
          Strict `=== false` keeps legacy users (where the field was
          never written) out of the dialog. */}
      <ChangePasswordModal
        open={user?.must_change_password === false}
        onSuccess={() => { /* updateUser() inside the modal flips
                              must_change_password to true, so this
                              component re-renders with the strict
                              `=== false` test failing → modal closes. */ }}
      />
    </ThemeProvider>
  );
};

export default MedicalDashboard;
